/**
 * Chart Export Utility
 * Provides functionality to export charts as images (PNG, SVG) or PDF
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { exportElementToPDF } from './pdfExport';

/**
 * Export chart element as PNG image
 * @param {HTMLElement} element - Chart element to export
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportChartAsPNG = async (element, filename = 'chart', options = {}) => {
  const {
    scale = 2,
    quality = 1.0,
    backgroundColor = '#ffffff'
  } = options;

  try {
    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor,
      width: element.offsetWidth,
      height: element.offsetHeight
    });

    canvas.toBlob((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 'image/png', quality);
  } catch (error) {
    console.error('Error exporting chart as PNG:', error);
    throw error;
  }
};

/**
 * Export chart element as SVG
 * @param {HTMLElement} element - Chart element to export (must contain SVG)
 * @param {string} filename - Filename (without extension)
 */
export const exportChartAsSVG = (element, filename = 'chart') => {
  try {
    // Find SVG element within the chart container
    const svgElement = element.querySelector('svg');
    
    if (!svgElement) {
      throw new Error('No SVG element found in chart');
    }

    // Clone the SVG to avoid modifying the original
    const clonedSvg = svgElement.cloneNode(true);
    
    // Get computed styles and apply them
    const styles = window.getComputedStyle(element);
    const svgStyles = window.getComputedStyle(svgElement);
    
    // Create a wrapper SVG with proper dimensions
    const wrapper = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    wrapper.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    wrapper.setAttribute('width', svgElement.getAttribute('width') || svgElement.viewBox.baseVal.width);
    wrapper.setAttribute('height', svgElement.getAttribute('height') || svgElement.viewBox.baseVal.height);
    
    // Copy styles if needed
    if (svgStyles.fill) wrapper.setAttribute('fill', svgStyles.fill);
    if (svgStyles.stroke) wrapper.setAttribute('stroke', svgStyles.stroke);
    
    // Append cloned content
    while (clonedSvg.firstChild) {
      wrapper.appendChild(clonedSvg.firstChild);
    }
    
    // Serialize SVG to string
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(wrapper);
    
    // Create blob and download
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting chart as SVG:', error);
    throw error;
  }
};

/**
 * Export chart element as PDF
 * @param {HTMLElement} element - Chart element to export
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportChartAsPDF = async (element, filename = 'chart', options = {}) => {
  return await exportElementToPDF(element, filename, options);
};

/**
 * Export multiple charts as a single PDF document
 * @param {Array} chartElements - Array of chart elements
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportChartsAsPDF = async (chartElements, filename = 'charts', options = {}) => {
  const {
    format = 'a4',
    orientation = 'portrait',
    margin = 10,
    quality = 1.0,
    scale = 2
  } = options;

  try {
    const pdf = new jsPDF({
      orientation: orientation === 'landscape' ? 'landscape' : 'portrait',
      unit: 'mm',
      format
    });

    const pageWidth = pdf.internal.pageSize.width;
    const pageHeight = pdf.internal.pageSize.height;
    const usableWidth = pageWidth - (margin * 2);
    const usableHeight = pageHeight - (margin * 2);

    for (let i = 0; i < chartElements.length; i++) {
      if (i > 0) {
        pdf.addPage();
      }

      const element = chartElements[i];
      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png', quality);
      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // If image is too tall, scale it down
      const finalHeight = imgHeight > usableHeight ? usableHeight : imgHeight;
      const finalWidth = (canvas.width * finalHeight) / canvas.height;

      const xPos = margin + (usableWidth - finalWidth) / 2;
      const yPos = margin;

      pdf.addImage(imgData, 'PNG', xPos, yPos, finalWidth, finalHeight);
    }

    pdf.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error exporting charts as PDF:', error);
    throw error;
  }
};




